import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { colors } from '@hefe/ui';
import { Button, Input, Select, Spin, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { FaEdit } from 'react-icons/fa';
import styles from './inline-edit-input.component.module.scss';

interface InlineEditInputProps {
  value: string | number | string[] | number[] | null | undefined | any;
  text?: string; // Extra text prop if value does not equal text to show
  disabled?: boolean;
  loading?: boolean;
  highlightingMode?: 'parent' | 'self';
  onOk: (value: any) => void;
  onModeChange?: (editMode: boolean) => void;
  component: any;
  componentProps?: any;
  customSelectItems?: any[];
}

export default function InlineEditInput(props: InlineEditInputProps) {
  const { disabled } = props;

  const [value, setValue] = useState(props.value);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef, (event) => {
    if (editMode) {
      // Trigger on ok
      if (value !== props.value) {
        onClickBtnOk(event);
      }
      // End edit mode
      updateEditMode(false);
      setParentBackgroundColor(event, 'transparent');
      event.stopPropagation();
    }
  });

  const disabledClass =
    ' ' + (props.loading || disabled ? styles['disabled'] : '');

  const onMouseOverViewMode = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    setParentBackgroundColor(event, colors.primary + '50');
  };

  const onMouseOutViewMode = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    setParentBackgroundColor(event, 'transparent');
  };

  const onClickBtnOk = (event: any) => {
    props.onOk(value);
    updateEditMode(false);
    event.stopPropagation();
  };

  const onClickBtnCancel = (event: any) => {
    setValue(props.value);
    updateEditMode(false);
    event.stopPropagation();
  };

  const onClickViewMode = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (!props.loading && !disabled) {
      updateEditMode(true);
      event.stopPropagation();
    }
  };

  const onClickInput = (event: any) => {
    event.stopPropagation();
  };

  const updateEditMode = (editMode: boolean) => {
    if (props.onModeChange) {
      props.onModeChange(editMode);
    }
    setEditMode(editMode);
  };

  const setParentBackgroundColor = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    color: string
  ) => {
    if (!disabled && !props.loading && props.highlightingMode === 'parent') {
      event.currentTarget.parentElement?.parentElement?.style.setProperty(
        'background',
        color
      );
    }
  };

  const renderViewMode = () => {
    return (
      <div
        className={styles['mode-view'] + disabledClass}
        onClick={(event) => onClickViewMode(event)}
        onMouseOver={(event) => onMouseOverViewMode(event)}
        onMouseOut={(event) => onMouseOutViewMode(event)}
      >
        {props.loading ? (
          <Spin size="small" />
        ) : (
          <Typography.Text
            style={{ color: disabled ? undefined : colors.primary }}
          >
            {props.text ?? value}
            {!props.disabled && <FaEdit style={{ marginLeft: 5 }} />}
          </Typography.Text>
        )}
      </div>
    );
  };

  const renderEditMode = () => {
    let component = undefined;
    if (props.component === Select) {
      component = (
        <props.component
          value={value}
          loading={props.component === Select ? props.loading : undefined}
          onClick={(event: any) => onClickInput(event)}
          onChange={(changeValue: any) => setValue(changeValue)}
          {...props.componentProps}
          style={{ flex: 1 }}
        >
          {props.component === Select &&
            props.customSelectItems &&
            props.customSelectItems}
        </props.component>
      );
    } else if (props.component === Input) {
      component = (
        <Input
          value={value}
          onChange={(changeValue) => setValue(changeValue.target.value)}
          {...props.componentProps}
          style={{ flex: 1 }}
        />
      );
    } else {
      component = (
        <props.component
          value={value}
          onChange={(changeValue: any) => setValue(changeValue)}
          {...props.componentProps}
          style={{ flex: 1 }}
        />
      );
    }
    return (
      <div style={{ display: 'flex', minWidth: 200 }}>
        {component}
        <Button
          onClick={(event) => onClickBtnOk(event)}
          icon={<CheckOutlined />}
        />
        <Button
          onClick={(event) => onClickBtnCancel(event)}
          icon={<CloseOutlined />}
        />
      </div>
    );
  };

  /***************************************************************************************************
   *
   *                                            MAIN RENDER
   *
   ***************************************************************************************************
   */
  const wrapperClassName =
    props.highlightingMode === 'self'
      ? !props.loading && !props.disabled
        ? styles['hover']
        : ''
      : '';
  return (
    <div ref={wrapperRef} className={`${wrapperClassName}`}>
      {editMode ? renderEditMode() : renderViewMode()}
    </div>
  );
}

/**
 * Hook that alerts clicks outside of the passed ref
 */
function useOutsideAlerter(ref: any, clickedOutside: (event: any) => void) {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: any) {
      if (ref.current && !ref.current.contains(event.target)) {
        clickedOutside(event);
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);
}
